import React, { ReactNode } from 'react';
import useUniqueId from '../_internal/useUniqueId';
import useScreenSize from '../_internal/useScreenSize';
import { ResponsiveValue, getResponsiveValue } from '../_internal/styling';
import Inline from '../Inline';
import Text from '../Text';
import SingleSelectBase from '../SingleSelectBase';
import ColorSwatch, { ColorSwatchType } from './ColorSwatch';
import ColorSwatchButton from './ColorSwatchButton';
import { TooltipProvider } from '../Tooltip';
import StopPropagation from '../Modal/StopPropagation';

interface Color extends ColorSwatchType {
  /**
   * Unique Color ID associated with the color
   */
  uniqueColorId?: string;
}

export interface ColorSwatchesProps {
  /**
   * Unique Color Id or color label of currently selected color swatch.
   */
  selectedColor: string;
  /**
   * An array of `ColorSwatchType` objects, each containing a color's corresponding
   * `label`, `hex` value, optional color image `url`, optional `uniqueColorId`, and an optional `isOutOfStock` boolean.
   * NOTE: teams using data from Shop Product Model must pass in `uniqueColorId`.
   */
  colors: Color[];
  /**
   * Callback function invoked when one of the color swatches is selected. When not provided, ColorSwatches are
   * rendered as static, and not interactive.
   * @param label the name of the color clicked.
   * @param uniqueColorId the unique color id of the color clicked, if available.
   */
  onChange?: (label: string, uniqueColorId?: string) => void;
  /**
   * Allows limiting the number of colors rendered.
   * By default, all colors will be rendered.
   * Responsive value, e.g., `{ sm: 3, md: 5 }`
   */
  limit?: ResponsiveValue<number>;
  /**
   * Whether or not the color swatches should wrap if they do not fit with the specified `spacing`
   */
  wrap?: ResponsiveValue<boolean>;
  /**
   * Label displayed after color swatches for the hidden colors. ie: '+2 More Colors'
   */
  hiddenColorsLabel?: string;
  /**
   * Size of interactive color swatches. `small` is intended for `ProductCard`s, `large` is for the
   * Product-details page (PDP).
   * @default 'large'
   */
  size?: 'small' | 'large';
  /**
   * Callback function invoked when a color swatch is hovered over, intended to preview the hero image on hover
   */
  onPreview?: (color: string) => void;
  /**
   * Callback function invoked when the user's cursor exits the ColorSwatches component area.
   */
  onPreviewEnd?: () => void;
}

/**
 * A collection of color swatches for a product.
 */
const ColorSwatches = ({
  selectedColor,
  colors,
  onChange,
  limit,
  wrap = false,
  hiddenColorsLabel = '',
  size = 'large',
  onPreview,
  onPreviewEnd,
}: ColorSwatchesProps): JSX.Element => {
  const colorSwatchesId = useUniqueId('color-swatches');
  const screenSize = useScreenSize();
  const colorLimit =
    (limit && getResponsiveValue(screenSize, limit)) ?? colors.length;
  const hiddenColorsCount = Math.max(colors.length - colorLimit, 0);
  const renderedColors = colors.slice(0, colorLimit);
  const colorOptions = renderedColors.map(color => ({
    // KNOWN A11Y ISSUE: once upstream data stops de-duping,
    // this will result in duplicate color names for aria-labels
    value: color.uniqueColorId || color.label,
    ...color,
  }));

  const isStatic = !onChange;
  const spacing = (() => {
    if (isStatic) return 0;

    return 0.125;
  })();

  const container = (children: ReactNode) => (
    <StopPropagation>
      <Inline
        onMouseLeave={onPreviewEnd}
        role={isStatic ? '' : 'radiogroup'}
        width="fit-content"
        aria-label="Color Swatches"
        spacing={spacing}
        vAlign="center"
        data-testid="color-swatches"
        wrap={wrap}
        /* For focus outline of selected color swatch */
        pb={isStatic ? 0 : 0.2}
      >
        {children}
        {hiddenColorsCount > 0 && (
          <Text
            height="short"
            scale="1"
            color="gray-35"
            data-testid="hidden-colors-label"
          >{`+${hiddenColorsCount} ${hiddenColorsLabel}`}</Text>
        )}
      </Inline>
    </StopPropagation>
  );

  if (isStatic) {
    return container(
      renderedColors.map((color, index) => (
        <ColorSwatch
          // eslint-disable-next-line react/no-array-index-key
          key={`${color.label}-${index}`}
          label={color.label}
          hex={color.hex}
          url={color.url}
          isOutOfStock={color.isOutOfStock}
          isSelected={
            color.label === selectedColor ||
            color.uniqueColorId === selectedColor
          }
          type="static"
        />
      )),
    );
  }

  const handleColorSwatchChange = (
    selectedColorLabelOrUniqueColorId: string,
  ) => {
    const colorWithUniqueId = colors.find(
      color => color.uniqueColorId === selectedColorLabelOrUniqueColorId,
    );

    // if the value of the selected color is a unique color id, we return the
    // human-readable name of the color ("label") alongside the uniqueColorId.
    if (colorWithUniqueId) {
      return onChange(colorWithUniqueId.label, colorWithUniqueId.uniqueColorId);
    }

    return onChange(selectedColorLabelOrUniqueColorId);
  };

  return (
    <TooltipProvider>
      <div onBlur={onPreviewEnd}>
        <SingleSelectBase
          container={container}
          name={colorSwatchesId}
          onChange={handleColorSwatchChange}
          options={colorOptions}
          value={selectedColor}
        >
          {({ isSelected, value, ...selectableProps }, currentColor) => {
            return (
              <div
                onMouseOver={() => onPreview?.(currentColor.value)}
                // Accessibility requirement. Ensures that keyboard users can also trigger the same behavior.
                onFocus={() => onPreview?.(currentColor.value)}
              >
                <ColorSwatchButton
                  // we hide the tooltip when the swatch is previewing on hover
                  hideTooltip={!!onPreview}
                  label={currentColor.label}
                  hex={currentColor.hex}
                  url={currentColor.url}
                  isSelected={isSelected}
                  isOutOfStock={currentColor.isOutOfStock}
                  value={value}
                  size={size}
                  skipProvider
                  {...selectableProps}
                />
              </div>
            );
          }}
        </SingleSelectBase>
      </div>
    </TooltipProvider>
  );
};

export default ColorSwatches;
